import {useGet, usePatch, usePost} from "@hooks/http.ts";
import {useCallback} from "react";
import {Action, Condition, RuleStatus, RuleType, Sequence} from "@types";
import {useOnce} from "@hooks/once.ts";


export type RuleCreateRequest = {
    name: string,
    rule: RuleType,
    sequence: {
        condition: Condition,
        action: Action
    }
};

export type RuleCreateResponse = {
    id: string
};

type RuleCreateAction = (req: RuleCreateRequest, abortController?: AbortController) => void
export const useRuleCreate = (): [
        RuleCreateResponse | undefined,
    boolean,
        Error | undefined,
    RuleCreateAction
] => {
    const [response, loading, error, post] = usePost<RuleCreateResponse>()
    const fn = useCallback(async (req: RuleCreateRequest, abortController?: AbortController) =>
        post(`/v1/rules`, req, abortController), [post]
    )
    return [response, loading, error, fn] as const;
}


export type RuleUpdateRequest = {
    name?: string,
    status?: RuleStatus,
    sequence?: {
        condition: Condition,
        action: Action
    }
};

export type RuleUpdateResponse = {};

type RuleUpdateAction = (id: string, req: RuleUpdateRequest, abortController?: AbortController) => void
export const useRuleUpdate = (): [
        RuleUpdateResponse | undefined,
    boolean,
        Error | undefined,
    RuleUpdateAction
] => {
    const [response, loading, error, patch] = usePatch<RuleUpdateResponse>()
    const fn = useCallback(async (id: string, req: RuleUpdateRequest, abortController?: AbortController) =>
        patch(`/v1/rules/${id}`, req, abortController), [patch]
    )
    return [response, loading, error, fn] as const;
}


export type RuleListResponse = {
    rules: Array<{
        id: string;
        name: string;
        status: RuleStatus;
        rule: RuleType;
    }>
}

type RuleListAction = (abortController?: AbortController) => void;

export const useRuleList = (): readonly [Array<{
    id: string;
    name: string;
    status: RuleStatus;
    rule: RuleType;
}> | undefined, boolean, Error | undefined, RuleListAction] => {
    const [response, loading, error, get] = useGet<RuleListResponse>();
    const fetchRules = useCallback((abortController?: AbortController) => {
        console.trace("fetching rules");
        get(`/v1/rules`, abortController);
    }, [get]);

    useOnce(() => {
        fetchRules()
    }, [fetchRules]);

    return [response?.rules, loading, error, fetchRules] as const;
}

export type RuleGetResponse = {
    id: string;
    name: string;
    status: RuleStatus;
    rule: RuleType;
    sequence: Sequence;
}

type RuleGetAction = (id: string | undefined, abortController?: AbortController) => void;
export const useRuleGet = (
    id: string | undefined
): [
        RuleGetResponse | undefined,
    boolean,
        Error | undefined,
    RuleGetAction
] => {
    const [response, loading, error, get] = useGet<RuleGetResponse>();

    const fetchRule = useCallback(
        (id: string | undefined, abortController?: AbortController) => {
            console.trace("fetching rule", id);
            get(`/v1/rules/${id}`, abortController);
        },
        [get]
    );

    useOnce(() => {
        if (id) {
            fetchRule(id);
        }
    }, [fetchRule, id]);

    return [response, loading, error, fetchRule] as const;
};